import React, { useEffect, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLoader } from '../../../../context/LoaderContext';
import { popup } from '../../../../utils';
import { Button, PopUpBox, ThresholdWarningPopup } from '../../../shared';
import {
  batchUpsert,
  batchDelete,
  readyForPack,
  readyForDye,
  getPackageList,
} from '../../../../service/manufacturing';
import { DeleteBatchPopup } from './BatchPopups';
import { getBatchQty } from './ManufacturingContent';
import {
  BUTTON,
  HEADING,
  POPUP_TYPE,
  STATUS,
  EDIT_BOX_STATE,
} from '../../../../constants';
import { useInProgressLotTracking } from './hooks/useInProgressLotTracking';
import { useBatchForm } from './hooks/useBatchForm';
import {
  BatchFormContainer,
  CuttingFieldsGroup,
  SewingFieldsGroup,
  PackingFieldsGroup,
  BatchFormActions
} from './components';

/**
 * BatchUpsert - Unified component for creating and editing batches using upsert endpoint
 *
 * This component handles both create and update operations using a single upsert endpoint.
 * Uses the useBatchForm hook for consolidated form state management and validation.
 *
 * Key Features:
 * - Single API call (upsert) instead of multiple calls
 * - Unified form state via useBatchForm hook
 * - Supports all stages: cutting, sewing, packing
 * - Atomic operations (all-or-nothing)
 *
 * @param {Object} lot - The lot object
 * @param {Object} selectedBatch - If provided, edit mode; if null, create mode
 * @param {string} field - Current editing stage (STATUS.CUT, STATUS.SEW, STATUS.PACK, STATUS.READY)
 * @param {Function} setField - Function to set the field being edited
 * @param {string} reportType - Type of report for create mode ('cutting' or 'sewing')
 * @param {Function} setEditBoxState - Function to control edit box state
 * @param {Function} setReload - Function to trigger data reload
 * @param {Function} setBlpData - Function to set BLP data
 * @param {boolean} dyeing - Whether dyeing is enabled
 * @param {boolean} isSaveLoading - Whether save operation is in progress
 * @param {Function} setIsSaveLoading - Function to control save loading state
 */

export const BatchUpsert = ({
  lot,
  selectedBatch = null,
  field = STATUS.CUT,
  setField,
  reportType = 'cutting',
  setEditBoxState,
  setReload,
  setBlpData,
  dyeing,
  isSaveLoading,
  setIsSaveLoading
}) => {
  // Determine edit mode from props (used in onFieldChange callback)
  const isEditMode = !!selectedBatch;

  // Memoized callback to prevent unnecessary re-renders in useBatchForm
  const handleFieldChange = useCallback(() => {
    // Update field status when user edits sewing or packing fields
    if (isEditMode) {
      if (field !== STATUS.SEW && field !== STATUS.PACK) {
        // Auto-detect which field is being edited based on batch state
        if (!selectedBatch?.sew_qty) {
          setField(STATUS.SEW);
        }
      }
    }
  }, [isEditMode, field, selectedBatch?.sew_qty, setField]);

  // Use consolidated form hook
  const form = useBatchForm({
    lot,
    selectedBatch,
    reportType,
    onFieldChange: handleFieldChange
  });

  const [showPopup, setShowPopup] = useState(false);
  const [showThresholdWarning, setShowThresholdWarning] = useState(false);
  const [thresholdWarningData, setThresholdWarningData] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);
  const { setLoading } = useLoader();
  const { trackLot, untrackLot } = useInProgressLotTracking(lot?.lot_no);

  /**
   * Auto-focus on first editable field when form opens
   */
  const { isCreateMode, isEditMode: formIsEditMode, focusField } = form;
  useEffect(() => {
    if (isCreateMode) {
      focusField();
    } else if (formIsEditMode) {
      focusField(field === STATUS.SEW ? 'sew' : field === STATUS.PACK ? 'pack' : null);
    }
  }, [isCreateMode, formIsEditMode, focusField, field]);

  /**
   * Auto-show the BLP list for packed, non-dyeing batches instead of the action buttons.
   * An empty or failed fetch (fetchData already toasts) falls back to the buttons view.
   * `cancelled` guards a late resolve from reopening the BLP view after the user has moved
   * on (e.g. closed the edit box) — those setters live in the parent (LotInfo), so React
   * gives no unmount warning to catch it.
   * Depends only on the batch id by design: refetching on every `selectedBatch` identity
   * change (e.g. a reload-triggered re-render) would loop the fetch; once this effect flips
   * the view via EDIT_BOX_STATE.BLP, BatchUpsert unmounts, so it never needs to react again.
   */
  useEffect(() => {
    let cancelled = false;
    if (selectedBatch?.state === 'packed' && !dyeing) {
      setLoading(true);
      getPackageList({ data: { batch_id: [selectedBatch.id] } }).then((response) => {
        if (cancelled) return;
        setLoading(false);
        if (response?.data?.length > 0) {
          setBlpData(response.data);
          setEditBoxState(EDIT_BOX_STATE.BLP);
        }
      });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatch?.id]);

  /**
   * Handle create operation using upsert endpoint
   */
  const handleCreate = useCallback(() => {
    // Validate
    const validation = form.validate();
    if (!validation.valid) {
      popup(POPUP_TYPE.ERR, validation.message);
      return;
    }

    // Build and send payload
    const payload = form.buildCreatePayload();

    batchUpsert({ data: payload })
      .then((response) => {
        if (response?.status === 'warning' && response?.warning_type === 'batch_threshold_exceeded') {
          setThresholdWarningData(response);
          setPendingPayload(payload);
          setShowThresholdWarning(true);
          return;
        }
        
        const reportName = form.isSewingReport ? 'Sewing Report' : 'Cutting Report';
        popup(POPUP_TYPE.SUCCESS, `${reportName} has been created successfully.`);
        setReload(true);
        setEditBoxState(EDIT_BOX_STATE.CLOSED);
      })
      .catch((error) => {
        popup(POPUP_TYPE.ERR, error.message);
        setLoading(false);
        setEditBoxState(EDIT_BOX_STATE.CLOSED);
      });
  }, [form, setReload, setEditBoxState, setLoading]);

  /**
   * Execute the batch update API call
   * Validates and sends the update payload for sewing or packing fields
   */
  const executeUpdate = useCallback(() => {
    const updateField = field === STATUS.SEW ? 'sew' : field === STATUS.PACK ? 'pack' : null;

    if (!updateField) {
      setIsSaveLoading(false);
      return;
    }

    // Validate
    const validation = form.validate(updateField);
    if (!validation.valid) {
      popup(POPUP_TYPE.ERR, validation.message);
      setIsSaveLoading(false);
      return;
    }

    // Build and send payload
    const payload = form.buildUpdatePayload(updateField);

    if (payload) {
      batchUpsert({ data: payload })
        .then((response) => {
          if (response?.status === 'warning' || response?.result?.status === 'warning') {
            const warningData = response?.result || response;
            setThresholdWarningData(warningData);
            setPendingPayload(payload);
            setShowThresholdWarning(true);
            setIsSaveLoading(false);
            return;
          }
          
          popup(POPUP_TYPE.SUCCESS, response?.message);
          setReload(true);
        })
        .catch((err) => {
          popup(POPUP_TYPE.ERR, err.message);
          setLoading(false);
        })
        .finally(() => {
          if (!showThresholdWarning) {
            untrackLot();
            setIsSaveLoading(false);
            setLoading(false);
            setEditBoxState(EDIT_BOX_STATE.CLOSED);
          }
        });
    } else {
      untrackLot();
      setIsSaveLoading(false);
    }
  }, [form, field, setEditBoxState, setReload, setIsSaveLoading, untrackLot, showThresholdWarning, setLoading]);

  /**
   * Handle edit mode submission
   * Loading state prevents double-clicks, so debounce is not needed
   */
  const handleSubmit = () => {
    setIsSaveLoading(true);
    trackLot();

    if ((field === STATUS.READY || field === STATUS.PACK) && dyeing === false) {
      // Ready for packing flow
      // If pack_qty doesn't exist yet, save it first before calling readyForPack
      const needsPackSave = !selectedBatch.pack_qty;
      const packSavePromise = needsPackSave
        ? batchUpsert({ data: form.buildUpdatePayload('pack') })
        : Promise.resolve();

      packSavePromise
        .then(() => readyForPack({ data: { batch_id: [selectedBatch.id] } }))
        .then((response) => {
          untrackLot();
          setIsSaveLoading(false);
          if (response) {
            popup(POPUP_TYPE.SUCCESS, response?.message);
            setEditBoxState(EDIT_BOX_STATE.BLP);
            setBlpData(response?.data);
            setReload(true);
          } else {
            setEditBoxState(EDIT_BOX_STATE.CLOSED);
          }
        })
        .catch(() => {
          untrackLot();
          setIsSaveLoading(false);
          setLoading(false);
        });
    } else if (field === STATUS.READY && dyeing === true) {
      // Ready for dyeing flow
      readyForDye({ data: { batch_id: [selectedBatch.id] } })
        .then((response) => {
          untrackLot();
          popup(POPUP_TYPE.SUCCESS, response?.message);
          setIsSaveLoading(false);
          setEditBoxState(EDIT_BOX_STATE.CLOSED);
          setReload(true);
        })
        .catch(() => {
          untrackLot();
          setIsSaveLoading(false);
          setLoading(false);
        });
    } else {
      // Update quantity flow
      executeUpdate();
    }
  };

  /**
   * Handle batch deletion
   */
  const handleBatchDelete = () => {
    batchDelete({ data: { batch_id: selectedBatch.id } })
      .then((response) => {
        popup(POPUP_TYPE.SUCCESS, response?.message);
        setReload(true);
      })
      .catch((err) => {
        popup(POPUP_TYPE.ERR, err.message);
      })
      .finally(() => {
        setLoading(false);
        setEditBoxState(EDIT_BOX_STATE.CLOSED);
      });
  };

  /**
   * Determine component heading based on mode
   */
  const getHeading = () => {
    if (form.isEditMode) {
      return HEADING.EDIT_BATCH;
    } else if (form.isSewingReport) {
      return 'Sewing Report Creation';
    } else {
      return HEADING.BATCH_CREATION;
    }
  };

  // Memoized values for field group props
  const cuttingValues = useMemo(() => ({
    batchQty: form.fields.cutting.qty,
    batchDmgQty: form.fields.cutting.dmgQty,
    cutDate: form.fields.cutting.date
  }), [form.fields.cutting.qty, form.fields.cutting.dmgQty, form.fields.cutting.date]);

  const cuttingRefs = useMemo(() => ({
    batchQuantity: form.fields.cutting.qtyRef,
    batchDmgQuantity: form.fields.cutting.dmgQtyRef
  }), [form.fields.cutting.qtyRef, form.fields.cutting.dmgQtyRef]);

  const sewingValues = useMemo(() => ({
    sewQty: form.fields.sewing.qty,
    sewDmgQty: form.fields.sewing.dmgQty,
    sewDate: form.fields.sewing.date
  }), [form.fields.sewing.qty, form.fields.sewing.dmgQty, form.fields.sewing.date]);

  const sewingRefs = useMemo(() => ({
    sewQuantity: form.fields.sewing.qtyRef,
    sewDmgQuantity: form.fields.sewing.dmgQtyRef
  }), [form.fields.sewing.qtyRef, form.fields.sewing.dmgQtyRef]);

  const packingValues = useMemo(() => ({
    pckQty: form.fields.packing.qty,
    pckDate: form.fields.packing.date
  }), [form.fields.packing.qty, form.fields.packing.date]);

  const packingRefs = useMemo(() => ({
    pckQuantity: form.fields.packing.qtyRef
  }), [form.fields.packing.qtyRef]);

  /**
   * Handle threshold warning confirmation
   */
  const handleThresholdWarningConfirm = useCallback(() => {
    if (pendingPayload) {
      // Add confirmation flag to payload
      const confirmedPayload = {
        ...pendingPayload,
        force_submit: true
      };
      
      batchUpsert({ data: confirmedPayload })
        .then(() => {
          const reportName = form.isSewingReport ? 'Sewing Report' : 'Cutting Report';
          const successMessage = form.isCreateMode 
            ? `${reportName} has been created successfully.`
            : 'Batch updated successfully.';
          popup(POPUP_TYPE.SUCCESS, successMessage);
          setReload(true);
          setEditBoxState(EDIT_BOX_STATE.CLOSED);
        })
        .catch((error) => {
          popup(POPUP_TYPE.ERR, error.message);
          setLoading(false);
          setEditBoxState(EDIT_BOX_STATE.CLOSED);
        })
        .finally(() => {
          setShowThresholdWarning(false);
          setThresholdWarningData(null);
          setPendingPayload(null);
          if (form.isEditMode) {
            untrackLot();
            setIsSaveLoading(false);
            setLoading(false);
          }
        });
    }
  }, [pendingPayload, form.isSewingReport, form.isCreateMode, form.isEditMode, setReload, setEditBoxState, untrackLot, setIsSaveLoading, setLoading]);

  /**
   * Handle threshold warning cancellation
   */
  const handleThresholdWarningCancel = useCallback(() => {
    setShowThresholdWarning(false);
    setThresholdWarningData(null);
    setPendingPayload(null);
    if (form.isEditMode) {
      untrackLot();
      setIsSaveLoading(false);
      setLoading(false);
      setEditBoxState(EDIT_BOX_STATE.CLOSED);
    }
  }, [form.isEditMode, untrackLot, setIsSaveLoading, setEditBoxState, setLoading]);

  return (
    <BatchFormContainer>
      <h5 className="text-center mb-4 mt-0">{getHeading()}</h5>
      <div className="row mx-0 edit-set-box">
        {/* Batch Number */}
        <div className="col-4">Batch No</div>
        <div className="col-8">
          {form.isEditMode ? selectedBatch.name : `${lot.lot_no}-${form.nextBatchNumber}`}
        </div>

        {/* Cutting Fields - Create mode for cutting reports */}
        {(form.isCreateMode && form.isCuttingReport) && (
          <CuttingFieldsGroup
            mode="create"
            values={cuttingValues}
            onCutQtyChange={form.fields.cutting.onQtyChange}
            onCutDmgQtyChange={form.fields.cutting.onDmgQtyChange}
            onCutDateChange={form.fields.cutting.onDateChange}
            refs={cuttingRefs}
            lot={lot}
            getBatchQty={getBatchQty}
          />
        )}

        {/* Cutting Fields - Readonly in edit mode if batch has cutting data */}
        {form.isEditMode && selectedBatch?.cut_qty > 0 && (
          <CuttingFieldsGroup
            mode="readonly"
            batchData={selectedBatch}
          />
        )}

        {/* Sewing Fields - Create mode for sewing reports */}
        {(form.isCreateMode && form.isSewingReport) && (
          <SewingFieldsGroup
            mode="create"
            values={sewingValues}
            onSewQtyChange={form.fields.sewing.onQtyChange}
            onSewDmgQtyChange={form.fields.sewing.onDmgQtyChange}
            onSewDateChange={form.fields.sewing.onDateChange}
            refs={sewingRefs}
            availableCapacity={form.availableSewCapacity}
          />
        )}

        {/* Sewing Fields - Edit mode */}
        {form.isEditMode && (
          <SewingFieldsGroup
            mode={field === STATUS.SEW ? 'edit' : 'readonly'}
            values={sewingValues}
            onSewQtyChange={form.fields.sewing.onQtyChange}
            onSewDmgQtyChange={form.fields.sewing.onDmgQtyChange}
            onSewDateChange={form.fields.sewing.onDateChange}
            refs={sewingRefs}
            availableCapacity={form.availableSewCapacity}
            batchData={selectedBatch}
            minDate={form.fields.sewing.minDate}
            isEditable={field === STATUS.SEW}
          />
        )}

        {/* Packing Fields - Edit mode only, not dyeing */}
        {form.isEditMode && !dyeing && (
          <PackingFieldsGroup
            mode={field === STATUS.PACK ? 'edit' : 'readonly'}
            values={packingValues}
            onPackQtyChange={form.fields.packing.onQtyChange}
            onPackDateChange={form.fields.packing.onDateChange}
            refs={packingRefs}
            batchData={selectedBatch}
            minDate={form.fields.packing.minDate}
            isEditable={field === STATUS.PACK}
          />
        )}

        {/* Lot Display - Create mode only */}
        {form.isCreateMode && (
          <>
            <div className="col-4">Lot</div>
            <div className="col-8">{form.selectedLot?.name || 'N/A'}</div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      {form.isCreateMode ? (
        <BatchFormActions
          onPrimaryAction={handleCreate}
          primaryLabel={form.isSewingReport ? BUTTON.CREATE_SEW_REPORT : BUTTON.CREATE_BATCH}
          onClose={() => setEditBoxState(EDIT_BOX_STATE.CLOSED)}
        />
      ) : (
        <div className="d-flex align-items-center justify-content-center gap-4 w-100 mt-4">
          {selectedBatch.state !== 'packed' ? (
            <>
              <Button
                clickHandler={handleSubmit}
                disabled={isSaveLoading}
                className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
                {field === STATUS.READY && dyeing
                  ? BUTTON.READY_FOR_DYEING
                  : (field === STATUS.READY || field === STATUS.PACK) && dyeing === false
                    ? BUTTON.READY_FOR_PACK
                    : BUTTON.SAVE}
              </Button>
              <Button
                clickHandler={() => setShowPopup(true)}
                className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
                {BUTTON.DELETE_BATCH}
              </Button>
            </>
          ) : null}
          <Button
            clickHandler={() => setEditBoxState(EDIT_BOX_STATE.CLOSED)}
            className="border-0 blue-btn d-flex align-items-center justify-content-center py-2 px-3">
            {BUTTON.CLOSE}
          </Button>
        </div>
      )}

      {showPopup && (
        <PopUpBox openPopUpBox={showPopup}>
          <DeleteBatchPopup apiCall={handleBatchDelete} setOpenConfirmationBox={setShowPopup} />
        </PopUpBox>
      )}
      
      {showThresholdWarning && thresholdWarningData && (
        <PopUpBox openPopUpBox={showThresholdWarning} modalWidth="50%">
          <ThresholdWarningPopup
            warningData={thresholdWarningData}
            onConfirm={handleThresholdWarningConfirm}
            onCancel={handleThresholdWarningCancel}
            allowEdit={false}
            warningType={thresholdWarningData?.warning_type === 'batch_threshold_exceeded' ? 'batch' : 'threshold'}
          />
        </PopUpBox>
      )}
    </BatchFormContainer>
  );
};

BatchUpsert.propTypes = {
  lot: PropTypes.object.isRequired,
  selectedBatch: PropTypes.object,
  field: PropTypes.string,
  setField: PropTypes.func,
  reportType: PropTypes.oneOf(['cutting', 'sewing']),
  setEditBoxState: PropTypes.func.isRequired,
  setReload: PropTypes.func.isRequired,
  setBlpData: PropTypes.func,
  dyeing: PropTypes.bool,
  isSaveLoading: PropTypes.bool,
  setIsSaveLoading: PropTypes.func
};
